import { useEffect, useRef, useState } from "react";
import { createCompLens, type LandingComp } from "./HeroCanvas.engine";

/**
 * The client-side hero animation island (implementation guide §6.1, ported to a plain Vite
 * component — no `"use client"` directive needed outside Next.js). Server/static-render
 * equivalent here: this file is the ONLY thing in `marketing/` that touches `useEffect` /
 * `requestAnimationFrame` — everything else in `sections.tsx` is a plain function component with
 * no client-only APIs, matching the guide's "only the HeroCanvas chunk is client JS" intent as
 * closely as a single-bundle Vite app can.
 */
/**
 * WHICH SCHEME IS PAINTING, RIGHT NOW (A-MARKETING, 2026-09-01).
 *
 * The same three-state question `app.css` answers in the cascade, asked in JS because a canvas is
 * not styled by the cascade. An explicit `data-theme` on `<html>` wins in both directions; with no
 * attribute the OS decides, which is the default state and the one a first-time visitor is in.
 *
 * TWO LISTENERS, BECAUSE THERE ARE TWO WAYS FOR THIS TO CHANGE: the OS flipping under a visitor
 * who has made no choice (matchMedia), and a theme control stamping the attribute (MutationObserver
 * — there is no event for an attribute change). Watching only the media query is the bug where the
 * app goes dark around a hero that stays light until a reload.
 *
 * `matchMedia` absent (jsdom without the setupTests stub, any non-browser render) resolves to
 * LIGHT, which is the palette the engine has always drawn and the one every existing test expects.
 */
function usePrefersDark(): boolean {
  const read = () => {
    if (typeof document === "undefined") return false;
    const stamped = document.documentElement.getAttribute("data-theme");
    if (stamped === "dark") return true;
    if (stamped === "light") return false;
    return typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
  };
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => setDark(read());
    sync();
    const mq = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    mq?.addEventListener("change", sync);
    let obs: MutationObserver | null = null;
    if (typeof MutationObserver !== "undefined") {
      obs = new MutationObserver(sync);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    }
    return () => {
      mq?.removeEventListener("change", sync);
      obs?.disconnect();
    };
  }, []);
  return dark;
}

export function HeroCanvas({ comps }: { comps: LandingComp[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dark = usePrefersDark();

  useEffect(() => {
    if (!ref.current) return;
    // `dark` is in the dep list on purpose: the static map is baked once into an offscreen canvas,
    // so a scheme change cannot be repainted — the engine is torn down and rebuilt instead.
    const engine = createCompLens(ref.current, { comps, dark });
    return () => engine.destroy();
  }, [comps, dark]);

  return (
    <canvas
      ref={ref}
      id="net"
      role="img"
      aria-label="Animated comparable-sales map. A lens drifts over city parcels; clicking briefly reveals nearby comps and a supported price range."
    />
  );
}
